import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Admin overrides for *static* UI dictionary strings.
 *
 * `t()` normally returns the pre-generated dictionary value, which means an
 * admin edit in Finnish Translations (stored in `ui_translations_cache`, keyed
 * by sha256 of the English source) would never show up. This function returns
 * any hand-saved value for the English UI sources so the language provider can
 * prefer it over the built-in dictionary.
 */
const Input = z.object({
  lang: z.string().min(2).max(5),
  sources: z.array(z.string().min(1).max(4000)).min(1).max(1200),
});

export const getUiOverrides = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => Input.parse(data))
  .handler(async ({ data }): Promise<{ overrides: Record<string, string> }> => {
    if (data.lang === "en") return { overrides: {} };
    const { createHash } = await import("crypto");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const hashOf = (s: string) => createHash("sha256").update(s).digest("hex").slice(0, 32);
    const byHash = new Map<string, string>();
    for (const s of data.sources) byHash.set(hashOf(s), s);

    const overrides: Record<string, string> = {};
    const hashes = Array.from(byHash.keys());
    for (let i = 0; i < hashes.length; i += 300) {
      const { data: rows } = await supabaseAdmin
        .from("ui_translations_cache")
        .select("source_hash,value")
        .eq("lang", data.lang)
        .in("source_hash", hashes.slice(i, i + 300));
      for (const row of rows ?? []) {
        const src = byHash.get(row.source_hash as string);
        const val = (row.value as string) ?? "";
        if (src && val.trim()) overrides[src] = val;
      }
    }
    return { overrides };
  });
